import { jsx, jsxs } from "react/jsx-runtime";
import { UserIcon } from "@dion-web/ui-icons";
import MBox from "@mui/material/Box";
import styled from "@mui/material/styles/styled";
import React, { useEffect, useState } from "react";
import { BLURHASH_DEFAULT_INITIAL_SIZE } from "../../shared/constants";
import { blurHashToBase64 } from "../../shared/helpers";
import { EThemeGradients } from "../../tokens/tokens";
import { abbreviate, getRadialGradient } from "./helpers";
import { Icon } from "../Icon";
import { useTheme } from "../ThemeProvider";
import Typography from "../Typography";
var EAvatarSize = /* @__PURE__ */ ((EAvatarSize2) => {
  EAvatarSize2["XS"] = "xs";
  EAvatarSize2["S"] = "s";
  EAvatarSize2["M"] = "m";
  EAvatarSize2["L"] = "l";
  EAvatarSize2["XL"] = "xl";
  EAvatarSize2["XXL"] = "xxl";
  EAvatarSize2["XXXL"] = "xxxl";
  return EAvatarSize2;
})(EAvatarSize || {});
var EAvatarVariant = /* @__PURE__ */ ((EAvatarVariant2) => {
  EAvatarVariant2["ROUNDED"] = "rounded";
  EAvatarVariant2["SQUARE"] = "square";
  return EAvatarVariant2;
})(EAvatarVariant || {});
var EAvatarColors = /* @__PURE__ */ ((EAvatarColors2) => {
  EAvatarColors2["ERROR"] = "error";
  EAvatarColors2["INFO"] = "info";
  EAvatarColors2["SUCCESS"] = "success";
  EAvatarColors2["WARNING"] = "warning";
  EAvatarColors2["ELEVATION_0"] = "elevation0";
  EAvatarColors2["ELEVATION_1"] = "elevation1";
  EAvatarColors2["ELEVATION_2"] = "elevation2";
  EAvatarColors2["ELEVATION_3"] = "elevation3";
  EAvatarColors2["ON_BRAND"] = "onBrand";
  EAvatarColors2["BRAND"] = "brand";
  EAvatarColors2["BLUE_ELEVATION_1"] = "blueElevation1";
  EAvatarColors2["BLUE_ELEVATION_2"] = "blueElevation2";
  return EAvatarColors2;
})(EAvatarColors || {});
const IMAGE_STYLE = { width: "100%", height: "100%", objectFit: "cover" };
const AvatarBox = styled(MBox)((props) => {
  const {
    size,
    variant,
    gradient,
    bg,
    color: propsColor,
    theme
  } = props;
  const avatarSize = theme.avatar.size.avatar[size];
  const avatarBorderRadius = theme.avatar.variant[variant]?.borderRadius;
  const showGradient = gradient && gradient !== EThemeGradients.NONE;
  const background = showGradient ? getRadialGradient(theme.palette.gradients[gradient]) : theme.palette[bg].transparent;
  const avatarColor = gradient === EThemeGradients.NONE ? theme.palette[bg].lowContrast : theme.palette.static.light;
  const color = propsColor && theme.palette[bg][propsColor];
  const avatarBoxSX = {
    minWidth: `${avatarSize}px`,
    minHeight: `${avatarSize}px`,
    maxWidth: `${avatarSize}px`,
    maxHeight: `${avatarSize}px`,
    borderRadius: `${avatarBorderRadius}%`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background,
    color: color ?? avatarColor,
    overflow: "hidden"
  };
  return { ...avatarBoxSX };
});
const AvatarSimple = (props) => {
  const {
    size = "l" /* L */,
    variant = "rounded" /* ROUNDED */,
    gradient = EThemeGradients.NONE,
    bg,
    color,
    imageSrc: imgSrc,
    desc,
    icon: IconToUse,
    blurHash,
    aspectRatio,
    loadDelay,
    className
  } = props;
  const theme = useTheme();
  const avatarTypography = theme.avatar.typography[size];
  const avatarLetterCount = theme.avatar.letterCount[size];
  const iconSize = theme.avatar.size.icon[size];
  const [isError, setIsError] = useState(false);
  const [sourceLoaded, setSourceLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState(imgSrc);
  const blurredImgSrc = React.useMemo(
    () => blurHash ? blurHashToBase64(blurHash, aspectRatio, BLURHASH_DEFAULT_INITIAL_SIZE, BLURHASH_DEFAULT_INITIAL_SIZE) : "",
    [blurHash]
  );
  const abbreviation = abbreviate(desc, avatarLetterCount);
  const hasDescription = !!abbreviation;
  const showDesc = hasDescription && !imgSrc;
  const showPlaceholder = !hasDescription && !imgSrc || isError;
  useEffect(() => {
    if (!imgSrc)
      return;
    if (sourceLoaded && imgSrc === imageSrc)
      return;
    setSourceLoaded(false);
    if (blurredImgSrc)
      setImageSrc(blurredImgSrc);
    const img = new Image();
    let timer;
    img.src = imgSrc;
    img.onload = () => {
      timer = setTimeout(() => {
        setImageSrc(imgSrc);
        setSourceLoaded(true);
      }, loadDelay ?? 0);
    };
    img.onerror = () => {
      setIsError(true);
      setImageSrc(void 0);
    };
    return () => {
      clearTimeout(timer);
    };
  }, [imgSrc]);
  const avatarColor = gradient === EThemeGradients.NONE ? theme.palette[bg].contrast : theme.palette.static.light;
  const FinalIconToUse = IconToUse ? IconToUse : /* @__PURE__ */ jsx(UserIcon, {});
  return /* @__PURE__ */ jsxs(
    AvatarBox,
    {
      size,
      variant,
      bg,
      color,
      gradient,
      className,
      children: [
        imgSrc && !isError && /* @__PURE__ */ jsx("img", { alt: desc, src: imageSrc, style: IMAGE_STYLE }),
        showDesc && /* @__PURE__ */ jsx(Typography, { variant: avatarTypography, color: color ?? avatarColor, allowHexColor: true, children: abbreviation }),
        showPlaceholder && // TODO (skoltsov) токенизировать дефолтную иконку
        /* @__PURE__ */ jsx(Icon, { size: iconSize, icon: FinalIconToUse })
      ]
    }
  );
};
var AvatarSimple_default = AvatarSimple;
export {
  EAvatarColors,
  EAvatarSize,
  EAvatarVariant,
  AvatarSimple_default as default
};
